import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Footer } from "@layouts/Footer";
import { Header } from "@layouts/Header";
import BusinessHeader from "@/features/business/components/business-header";
import { EmergencyCTASection } from "../../components";
import PolicyContentSection from "../../components/PolicyContentSection/PolicyContentSection";
import CookiesContentSection from "../../components/CookiesContentSection/CookiesContentSection";
import RightsContentSection from "../../components/RightsContentSection/RightsContentSection";

const tabs = [
  {
    id: "privacy" as const,
    label: "Privacy Policy",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2L4 5V11.09C4 16.14 7.41 20.85 12 22C16.59 20.85 20 16.14 20 11.09V5L12 2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M10 15.5L7.5 13L8.91 11.59L10 12.67L15.09 7.59L16.5 9L10 15.5Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    id: "cookies" as const,
    label: "Cookies Policy",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx="8" cy="10" r="1.5" fill="currentColor" />
        <circle cx="13" cy="8" r="1" fill="currentColor" />
        <circle cx="11" cy="14" r="1.5" fill="currentColor" />
        <circle cx="15.5" cy="13" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: "rights" as const,
    label: "Rights & Responsibilities",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M16 4H18C19.1046 4 20 4.89543 20 6V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V6C4 4.89543 4.89543 4 6 4H8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <rect
          x="8"
          y="2"
          width="8"
          height="4"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M9 12L11 14L15 10"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

type TabId = (typeof tabs)[number]["id"];

const heroContent: Record<TabId, { title: string; description: string }> = {
  privacy: {
    title: "Privacy Policy",
    description:
      "At GASIS, your privacy and data security are our top priorities. This policy explains how we collect, use, store, and protect your personal information.",
  },
  cookies: {
    title: "Cookies Policy",
    description:
      "This Cookies Policy explains how GASIS uses cookies and similar tracking technologies on our website to improve your browsing experience.",
  },
  rights: {
    title: "Your Rights. Our Responsibilities.",
    description:
      "At GASIS, we believe in transparency, safety, and customer care. This page outlines what you can expect from us — and what we expect from you.",
  },
};

export function PrivacyPolicy() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isBusiness = location.pathname.startsWith("/business");

  const initialTab = (searchParams.get("tab") as TabId) || "privacy";
  const [activeTab, setActiveTab] = useState<TabId>(
    tabs.some((t) => t.id === initialTab) ? initialTab : "privacy"
  );

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { title, description } = heroContent[activeTab];

  return (
    <div className="w-full flex flex-col items-start justify-center bg-white">
      {/* Hero */}
      <section className="w-full bg-[#82716E] overflow-hidden relative">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0  pointer-events-none" />

        <div className="w-full max-w-[1728px] mx-auto flex flex-col items-center pt-8 px-5 md:px-10 lg:px-[180px] pb-12 md:pb-16 lg:pb-[80px] relative z-10">
          <div className="w-full max-w-[1368px]">
            {isBusiness ? <BusinessHeader /> : <Header />}
          </div>

          <div className="w-full max-w-[1368px] flex flex-col items-center text-center pt-16 md:pt-20 lg:pt-[100px] gap-5 md:gap-6">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L4 5V11.09C4 16.14 7.41 20.85 12 22C16.59 20.85 20 16.14 20 11.09V5L12 2Z"
                  fill="white"
                  fillOpacity="0.8"
                />
                <path
                  d="M10 15.5L7.5 13L8.91 11.59L10 12.67L15.09 7.59L16.5 9L10 15.5Z"
                  fill="#122d63"
                />
              </svg>
              <span className="text-[13px] md:text-[15px] text-white/80 tracking-[-0.02em] font-medium font-albert-sans">
                Effective January 2026
              </span>
            </div>

            <h1 className="text-[36px] md:text-[52px] lg:text-[76px] font-bold tracking-[-0.04em] text-white font-albert-sans leading-[1.05]">
              {title}
            </h1>
            <p className="text-[16px] md:text-[20px] lg:text-[24px] tracking-[-0.04em] text-white/65 font-albert-sans leading-[1.4] max-w-[860px]">
              {description}
            </p>

            {/* Tab Navigation */}
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mt-6 md:mt-10">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full text-[13px] md:text-[16px] font-medium tracking-[-0.02em] font-albert-sans transition-all duration-300 cursor-pointer ${
                    activeTab === tab.id
                      ? "bg-white text-[#122d63] shadow-lg shadow-black/10"
                      : "bg-white/10 text-white/80 hover:bg-white/20 border border-white/15"
                  }`}
                >
                  <span
                    className={
                      activeTab === tab.id ? "text-[#f24d2b]" : "text-white/60"
                    }
                  >
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      {activeTab === "privacy" && <PolicyContentSection />}
      {activeTab === "cookies" && <CookiesContentSection />}
      {activeTab === "rights" && <RightsContentSection />}

      <EmergencyCTASection
        title="Have Questions About Your Data?"
        subtitle={
          <>
            Contact our Data Protection Officer for any privacy-related
            inquiries.
          </>
        }
      >
        <div className="flex shrink-0 flex-col items-start">
          <div className="rounded-[12px] md:rounded-[24px] bg-white p-3 md:p-6 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[14px] md:text-xs font-medium text-[#1c1c1c] md:text-nowrap">
                privacy@gasis.ae
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[14px] md:text-xs font-medium text-[#1c1c1c] md:text-nowrap">
                800-GASIS (42747)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[14px] md:text-xs font-medium text-[#1c1c1c] md:text-nowrap">
                Available 24/7
              </span>
            </div>
          </div>
        </div>
      </EmergencyCTASection>
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
